'use client';

import Link from 'next/link';
import { useState } from 'react';

import { useAddCartLines } from '@/entities/cart/client';
import {
  colorName,
  defaultColorId,
  defaultSize,
  findVariant,
  isColorAvailable,
  optionAvailability,
  optionsSummary,
  primaryMedia,
  productHref,
  ProductMedia,
  VariantPicker,
  type Product,
  type Selection,
} from '@/entities/product';
import { formatMoney } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogTitle } from '@/shared/ui/dialog';
import { ScrollRow } from '@/shared/ui/scroll-row';
import { TextButton } from '@/shared/ui/text-link';

import styles from './cart-recommendations.module.scss';

export type CartRecommendationsProps = {
  products: Product[];
  onAdded: (name: string) => void;
};

/** Card subtitle: size-grid products list their options; one-size items show the colour quick add will use (04B). */
function cardVariant(product: Product) {
  if (product.sizes.length <= 1 && product.colors.length > 0) {
    return [colorName(product, defaultColorId(product)), product.sizes[0]]
      .filter(Boolean)
      .join(' · ');
  }
  return optionsSummary(product);
}

/** "Дополните заказ": quick add, or a compact colour/size picker for items with a size grid. */
export function CartRecommendations({ products, onAdded }: CartRecommendationsProps) {
  const [picking, setPicking] = useState<Product | null>(null);
  const [selection, setSelection] = useState<Selection>({ colorId: null, size: null });
  const [tip, setTip] = useState('');
  const [error, setError] = useState('');
  const add = useAddCartLines();
  if (!products.length) return null;

  const addVariant = (product: Product, choice: Selection, after?: () => void) => {
    const variant = findVariant(product, choice);
    if (!variant || variant.availability === 'out_of_stock') {
      setTip('Этого варианта сейчас нет в наличии');
      return;
    }
    add.mutate(
      { lines: [{ sku: variant.sku, quantity: 1, position: null }] },
      {
        onSuccess: () => {
          onAdded(product.name);
          after?.();
        },
        onError: (failure) => (picking ? setTip(failure.message) : setError(failure.message)),
      },
    );
  };
  const quickAdd = (product: Product) => {
    setError('');
    const choice = { colorId: defaultColorId(product), size: defaultSize(product) };
    if (product.sizes.length > 1) {
      setPicking(product);
      setSelection({ colorId: choice.colorId, size: null });
      setTip('Выберите размер, чтобы добавить товар');
      return;
    }
    addVariant(product, choice);
  };
  const pickColor = (colorId: string) => {
    if (!picking) return;
    if (!isColorAvailable(picking, colorId)) {
      setTip('Этого цвета сейчас нет в наличии');
      return;
    }
    // The picker silently drops a size that the new colour lacks (04B pickerColor).
    if (selection.size && optionAvailability(picking, colorId, selection.size) === 'out_of_stock') {
      setSelection({ colorId, size: null });
      setTip('Выберите размер, чтобы добавить товар');
      return;
    }
    setSelection({ ...selection, colorId });
    setTip(selection.size ? '' : 'Выберите размер, чтобы добавить товар');
  };
  const pickSize = (size: string) => {
    if (!picking) return;
    if (optionAvailability(picking, selection.colorId, size) === 'out_of_stock') {
      setTip(`Размера ${size} сейчас нет в наличии`);
      return;
    }
    setSelection({ ...selection, size });
    setTip('');
  };
  const close = () => {
    setPicking(null);
    setTip('');
  };

  return (
    <section className={styles.root} aria-labelledby="recs-title">
      <ScrollRow
        heading={
          <h2 id="recs-title" className={styles.title}>
            Дополните заказ
          </h2>
        }
        prevLabel="Предыдущие товары"
        nextLabel="Следующие товары"
        listLabel="Рекомендации"
        className={styles.row}
      >
        {products.map((product) => (
          <article key={product.id} className={styles.card}>
            <Link href={productHref(product)} aria-label={product.name} tabIndex={-1}>
              <ProductMedia
                media={primaryMedia(product)}
                fallbackLabel={product.name}
                sizes="296px"
                className={styles.photo}
                placeholderSub=""
                sketchSize="62%"
                position="center top"
              />
            </Link>
            <Link href={productHref(product)} className={styles.name}>
              {product.name}
            </Link>
            <span className={styles.variant}>{cardVariant(product)}</span>
            <span className={styles.price}>{product.price ? formatMoney(product.price) : '—'}</span>
            <TextButton
              className={styles.cta}
              onClick={() => quickAdd(product)}
              disabled={add.isPending}
              aria-label={`${product.sizes.length > 1 ? 'Выбрать размер' : 'Добавить'}: ${product.name}`}
            >
              {product.sizes.length > 1 ? 'Выбрать размер' : 'Добавить'}
            </TextButton>
          </article>
        ))}
      </ScrollRow>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <Dialog
        theme="editorial"
        open={picking !== null}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        title={picking?.name ?? 'Выбор варианта'}
        ownTitle
        width="35rem"
        backdrop="picker"
        shadow="picker"
        mobileSheet
        className={styles.picker}
      >
        {picking &&
          (() => {
            const variant = findVariant(picking, selection);
            const ready = Boolean(
              selection.size && variant && variant.availability !== 'out_of_stock',
            );
            const price = variant?.price ?? picking.price;
            return (
              <div className={styles['picker-grid']}>
                <ProductMedia
                  media={primaryMedia(picking)}
                  fallbackLabel={picking.name}
                  sizes="180px"
                  className={styles['picker-photo']}
                  placeholderSub=""
                  sketchSize="62%"
                  position="center top"
                />
                <div className={styles['picker-body']}>
                  <div className={styles['picker-head']}>
                    <div className={styles['picker-title']}>
                      <DialogTitle className={styles['picker-name']}>{picking.name}</DialogTitle>
                      <span>{price ? formatMoney(price) : '—'}</span>
                    </div>
                    <button
                      type="button"
                      className={styles.close}
                      onClick={close}
                      aria-label="Закрыть"
                    >
                      ×
                    </button>
                  </div>
                  <VariantPicker
                    idPrefix={`rec-${picking.slug}`}
                    product={picking}
                    colorId={selection.colorId}
                    size={selection.size}
                    onColorChange={pickColor}
                    onSizeChange={pickSize}
                    density="compact"
                    tip={tip}
                    className={styles.variants}
                  />
                  <Button
                    fullWidth
                    size="sm"
                    className={styles['picker-cta']}
                    loading={add.isPending}
                    aria-disabled={!ready || undefined}
                    onClick={() => {
                      if (!ready) {
                        setTip('Выберите размер');
                        return;
                      }
                      addVariant(picking, selection, close);
                    }}
                  >
                    Добавить в корзину{price ? ` — ${formatMoney(price)}` : ''}
                  </Button>
                </div>
              </div>
            );
          })()}
      </Dialog>
    </section>
  );
}
